import React, { useState, useEffect, useRef } from 'react'
import { DatePicker as BaseDatePicker } from '@mantine/dates'
import { useClickOutside } from '@mantine/hooks'
import { CalendarMonthOutlined } from '@mui/icons-material'
import cn from '../../lib/utils/cn'
import { useUserDateFormat } from '../../hooks/useUserDateFormat'

interface DatePickerProps {
	value: Date | null
	onChange: (date: Date | null) => void
	placeholder?: string
	wrapperClassName?: string
	className?: string
	classNameLabel?: string
	classNameCalendar?: string
	classNameCalendarWrapper?: string
	minDate?: Date
	maxDate?: Date
	formatTemplate?: string
	calendarIconPosition?: 'left' | 'right'
	label?: string
	labelClassName?: string
	dropdownPosition?: 'auto' | 'top' | 'bottom'
	required?: boolean
}

const DatePicker: React.FC<DatePickerProps> = ({
	value,
	onChange,
	placeholder = 'Select a date',
	className = '',
	wrapperClassName = '',
	classNameLabel = '',
	classNameCalendar,
	classNameCalendarWrapper,
	minDate,
	maxDate,
	formatTemplate = 'DD MMM YYYY',
	calendarIconPosition = 'left',
	label,
	labelClassName = '',
	dropdownPosition = 'auto',
	required,
}) => {
	const { formatDate } = useUserDateFormat()
	const [isOpen, setIsOpen] = useState(false)
	const [modalDirection, setModalDirection] = useState<
		'left' | 'right' | 'top' | null
	>(null)
	const [alignRight, setAlignRight] = useState(false)
	const [shouldLimitHeight, setShouldLimitHeight] = useState(false)
	const containerRef = useRef<HTMLDivElement>(null)
	const clickOutsideRef = useClickOutside(() => setIsOpen(false))

	useEffect(() => {
		if (!isOpen) return
		const handleResize = () => {
			setIsOpen(false)
		}

		const handleScroll = () => {
			setIsOpen(false)
		}

		window.addEventListener('resize', handleResize)
		window.addEventListener('scroll', handleScroll, true)

		return () => {
			window.removeEventListener('resize', handleResize)
			window.removeEventListener('scroll', handleScroll, true)
		}
	}, [isOpen])

	return (
		<div className={cn('relative w-full', wrapperClassName)} ref={containerRef}>
			{label && (
				<label
					className={cn(
						'block text-sm mb-1 text-text-main dark:text-text-main-dark',
						labelClassName
					)}
				>
					{label}

					{required && <span className="text-accent"> *</span>}
				</label>
			)}
			<div
				className={cn(
					'flex group items-center gap-2 box-border w-full px-3 py-2 border border-stroke-dark dark:border-stroke-light-dark rounded-lg bg-white dark:bg-background-dark dark:text-text-main-dark shadow-sm cursor-pointer',
					className
				)}
				onClick={() => {
					if (isOpen) {
						setIsOpen(false)
						setModalDirection(null)
						return
					}

					const container = containerRef.current
					if (container) {
						const rect = container.getBoundingClientRect()
						const spaceBelow = window.innerHeight - rect.bottom
						const spaceAbove = rect.top

						const popupWidth = 320
						const padding = 8
						const willOverflowRight =
							rect.left + popupWidth > window.innerWidth - padding
						setAlignRight(willOverflowRight)

						if (dropdownPosition === 'top') {
							setModalDirection('top')
							setShouldLimitHeight(false)
						} else if (dropdownPosition === 'bottom') {
							setModalDirection(null)
							setShouldLimitHeight(false)
						} else {
							// auto
							if (spaceBelow < 350 && spaceAbove > 350) {
								setModalDirection('top')
								setShouldLimitHeight(false)
							} else {
								setModalDirection(null)
								setShouldLimitHeight(spaceBelow < 350)
							}
						}
					}

					setTimeout(() => {
						setIsOpen(true)
					}, 100)
				}}
			>
				{calendarIconPosition === 'left' && (
					<CalendarMonthOutlined className={cn("w-5 h-5 fill-text-main dark:fill-text-main-dark group-hover:fill-accent dark:group-hover:fill-accent", isOpen && "fill-accent dark:fill-accent")} />
				)}
				<span
					className={cn(
						'flex-1',
						value
							? 'text-text-main dark:text-text-main-dark'
							: 'text-stroke-dark',
						classNameLabel
					)}
				>
					{value ? formatDate(value) : placeholder}
				</span>
				{calendarIconPosition === 'right' && (
					<CalendarMonthOutlined className={cn("w-5 h-5 fill-text-main dark:fill-text-main-dark group-hover:fill-accent dark:group-hover:fill-accent", isOpen && "fill-accent dark:fill-accent")} />
				)}
			</div>

			{isOpen && (
				<div
					className={cn(
						'absolute z-50 bg-white dark:text-text-main-dark dark:bg-background-dark border border-stroke-dark dark:border-stroke-light-dark rounded-lg shadow-lg p-2',
						alignRight ? 'right-0' : 'left-0',
						modalDirection === 'top' ? 'bottom-full mb-2' : 'top-full mt-2',
						shouldLimitHeight && 'max-h-64 overflow-y-auto',
						classNameCalendarWrapper,
					)}
					ref={clickOutsideRef}
				>
					<BaseDatePicker
						value={value}
						minDate={minDate}
						maxDate={maxDate}
						onChange={(date) => {
							onChange(date)
							setIsOpen(false)
						}}
						classNames={{
							month: cn('w-full', classNameCalendar),
							calendarHeader: 'bg-transparent',
							calendarHeaderControl:
								'bg-transparent w-10 h-10 hover:bg-background-light-hover dark:text-text-main-dark rounded',
							calendarHeaderLevel: 'bg-transparent dark:text-text-main-dark',
							monthThead: 'bg-background-light dark:bg-stroke-light-dark',
							day: 'text-text-main hover:bg-accent hover:text-white dark:text-text-main-dark rounded data-selected:bg-red-500 ',
						}}
					/>
				</div>
			)}
		</div>
	)
}

export default DatePicker
